(() => {
  const MEDIA_SELECTOR = '[data-ct-deferred-media]';
  const LOADED_ATTRIBUTE = 'data-ct-media-loaded';
  const observedMedia = new WeakMap();

  const markLoaded = (media) => {
    media.setAttribute(LOADED_ATTRIBUTE, 'true');
    media.dispatchEvent(new CustomEvent('ct:media-loaded', { bubbles: true }));
  };

  const loadImage = (media) => {
    const src = media.dataset.src;
    const srcset = media.dataset.srcset;

    if (!src && !srcset) return false;

    if (!media.dataset.ctMediaErrorHandler) {
      media.addEventListener('error', () => {
        if (!media.dataset.src || media.dataset.ctMediaFallback) return;

        media.dataset.ctMediaFallback = 'true';
        media.removeAttribute('srcset');
        media.removeAttribute('sizes');
        media.setAttribute('src', media.dataset.src);
      }, { once: true });
      media.dataset.ctMediaErrorHandler = 'true';
    }

    media.loading = 'eager';
    if (media.dataset.sizes) media.setAttribute('sizes', media.dataset.sizes);
    if (srcset) media.setAttribute('srcset', srcset);
    if (src) media.setAttribute('src', src);

    return true;
  };

  const loadVideo = (media) => {
    if (media.dataset.poster) media.poster = media.dataset.poster;

    media.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
    });

    media.preload = media.dataset.preload || 'metadata';
    media.load();
  };

  const loadMedia = (media) => {
    if (!media || media.hasAttribute(LOADED_ATTRIBUTE)) return false;

    let loaded = false;
    switch (media.dataset.ctDeferredMedia) {
      case 'image':
        loaded = loadImage(media);
        break;
      case 'video':
        loadVideo(media);
        loaded = true;
        break;
      default:
        return false;
    }

    if (!loaded) return false;
    markLoaded(media);
    return true;
  };

  const getMedia = (root) => {
    if (!root || typeof root.querySelectorAll !== 'function') return [];

    const media = [];
    if (root.matches?.(MEDIA_SELECTOR)) media.push(root);
    media.push(...root.querySelectorAll(MEDIA_SELECTOR));
    return media;
  };

  const isNearViewport = (media) => {
    const style = window.getComputedStyle(media);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    const rect = media.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;

    const verticalRange = window.innerHeight + 300;
    const horizontalRange = window.innerWidth;

    return (
      rect.bottom >= -300 &&
      rect.top <= verticalRange &&
      rect.right >= 0 &&
      rect.left <= horizontalRange
    );
  };

  const stopObserving = (media) => {
    observedMedia.get(media)?.();
    observedMedia.delete(media);
  };

  const isManualMedia = (media) => media.hasAttribute('data-ct-deferred-manual');

  const scanNearViewport = () => {
    getMedia(document).forEach((media) => {
      if (!media.hasAttribute(LOADED_ATTRIBUTE) && !isManualMedia(media) && isNearViewport(media)) {
        loadMedia(media);
        stopObserving(media);
      }
    });
  };

  const observerManager = window.ChewTrueIntersectionObserver;

  const observe = (root = document) => {
    getMedia(root).forEach((media) => {
      if (media.hasAttribute(LOADED_ATTRIBUTE) || isManualMedia(media)) return;

      if (!observerManager) {
        loadMedia(media);
        return;
      }

      if (!observedMedia.has(media)) {
        const unsubscribe = observerManager.observeOnce(media, () => {
          loadMedia(media);
          observedMedia.delete(media);
        }, {
          rootMargin: '300px 0px',
          threshold: 0.01,
        });
        observedMedia.set(media, unsubscribe);
      }
    });
  };

  window.ChewTrueDeferredMedia = {
    load: loadMedia,
    observe,
    scan: scanNearViewport,
  };

  const initialize = () => {
    observe(document);
    scanNearViewport();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    observe(event.target);
    scanNearViewport();
  });

  let scanFrame;
  const scheduleScan = () => {
    if (scanFrame) return;

    scanFrame = requestAnimationFrame(() => {
      scanFrame = undefined;
      scanNearViewport();
    });
  };

  window.addEventListener('scroll', scheduleScan, { passive: true });
  window.addEventListener('resize', scheduleScan, { passive: true });

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) observe(node);
      });
    });
  });

  mutationObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();